/**
 * @krisspy-file
 * @type page
 * @name "BookingEdit"
 * @title "Edit Booking"
 * @description "View and edit booking details"
 * @routes ["/bookings/:id/edit"]
 * @layout "DashboardLayout"
 * @flowName "booking-management"
 * @design "reference"
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertTriangle,
  AlertCircle,
} from 'lucide-react';
import { getBookingById, updateBooking, checkBookingItemsAvailability, checkPoolItemsAvailability, deleteBookingItems, createBookingItems, updateBookingItems } from '../services/bookings.service';
import { insertAuditLog, insertAuditLogBatch } from '../services/bookingAuditLog.service';
import { hasReturnReport } from '../services/return-reports.service';
import { calculatePrice, pricingRuleColumns, NO_PRICING_RULE, type ItemPricingRuleColumns } from '../services/pricing.service';
import { useAuth } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../lib/currency';
import { computeBookingTotals } from '../lib/bookingTotals';
import { pickChangedBookingFields, changedMoneyFields, moneyFieldsMovedSinceLoad } from '../lib/bookingSavePayload';
import type { Database } from '../lib/database.types';
import BookingGeneralForm from '../components/BookingGeneralForm';
import BookingItemsEditor from '../components/BookingItemsEditor';
import BookingDeliveryForm from '../components/BookingDeliveryForm';
import BookingFinancialView from '../components/BookingFinancialView';
import BookingNotesForm from '../components/BookingNotesForm';
import ReturnReportDetail from '../components/ReturnReportDetail';
import BookingEmailsTab from '../components/BookingEmailsTab';
import BookingMessagesTab from '../components/BookingMessagesTab';
import BookingAuditLog from '../components/BookingAuditLog';
import ResolveConflictsModal from '../components/ResolveConflictsModal';
import { useToast } from '../contexts/ToastContext';
import { useBarcodeScanning } from '../contexts/ScanContext';
import PickupChecklist, { type PickupScan } from '../components/scan/PickupChecklist';
import ScanModal from '../components/scan/ScanModal';
import { lineLabel, type PhysicalLine } from '../components/scan/scanLines';
import { onBookingUpdated } from '../lib/bookingEvents';

type Booking = Database['public']['Tables']['bookings']['Row'];
type BookingItem = Database['public']['Tables']['booking_items']['Row'];
type BookingExtension = Database['public']['Tables']['booking_extensions']['Row'];

interface BookingWithRelations extends Booking {
  locations?: any;
  booking_items?: Array<BookingItem & {
    product_types?: { product_name?: string; product_type?: string };
    inventory_items?: { nr?: number; item_number?: string; combination_code?: string };
  }>;
  booking_extensions?: readonly BookingExtension[];
}

type TabType = 'general' | 'items' | 'delivery' | 'financial' | 'notes' | 'emails' | 'messages' | 'returnInfo' | 'activity';

// Note: In this app, "returned" is the effective "completed" state.
const BOOKING_STATES = ['open', 'active', 'returned', 'cancelled'] as const;
// NOTE: PAYMENT_STATES currently unused (payment status editing will be added later)
// const PAYMENT_STATES = ['open', 'open_pay_later', 'paid', 'refunded', 'partially_refunded'] as const;

type RuleColumnsSource = {
  pricing_rule_id?: string | null;
  duration_type?: string | null;
  duration_value?: number | null;
  duration_label?: string | null;
};

/** The item's pricing-rule columns (see ItemPricingRuleColumns), absent read as NULL. */
function itemRuleColumns(item: RuleColumnsSource): ItemPricingRuleColumns {
  return {
    pricing_rule_id: item.pricing_rule_id ?? null,
    duration_type: item.duration_type ?? null,
    duration_value: item.duration_value ?? null,
    duration_label: item.duration_label ?? null,
  };
}

function ruleColumnsChanged(a: RuleColumnsSource, b: RuleColumnsSource): boolean {
  const x = itemRuleColumns(a);
  const y = itemRuleColumns(b);
  return x.pricing_rule_id !== y.pricing_rule_id
    || x.duration_type !== y.duration_type
    || x.duration_value !== y.duration_value
    || x.duration_label !== y.duration_label;
}

export default function BookingEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile, merchantId, locationIds, isPlatformAdmin, hasRole } = useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const routerLocation = useLocation();
  const { enabled: scanningEnabled } = useBarcodeScanning();
  // Pickup checklist (scanning merchants): lines not yet scanned, for the Start warning.
  const [pickupUnticked, setPickupUnticked] = useState<PhysicalLine[]>([]);
  const [showStartWarning, setShowStartWarning] = useState(false);
  const pickupScan = (routerLocation.state as { scan?: PickupScan } | null)?.scan ?? null;

  const [booking, setBooking] = useState<BookingWithRelations | null>(null);
  const [originalBooking, setOriginalBooking] = useState<BookingWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveWarning, setSaveWarning] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [datesChanged, setDatesChanged] = useState(false);
  const [returnReportExists, setReturnReportExists] = useState(false);
  // Set when a date/duration change leaves one or more items with no pricing
  // rule covering the new duration (their previous price is kept).
  const [stalePriceItems, setStalePriceItems] = useState<{ count: number; total: number } | null>(null);
  const [clearingTestFlag, setClearingTestFlag] = useState(false);

  // UI-only: show the "editing disabled" warning only when user attempts edits
  const [showEditDisabledWarning, setShowEditDisabledWarning] = useState(false);

  // Resolve-conflicts modal state
  const [showConflictsModal, setShowConflictsModal] = useState(false);
  const [pendingSaveAfterConflicts, setPendingSaveAfterConflicts] = useState(false);
  const [conflictingItemsForModal, setConflictingItemsForModal] = useState<Array<{
    bookingItemId: string;
    currentInventoryItemId: string;
    productTypeId: string;
    productName: string;
    currentSize: string | null;
    currentItemNumber: string | null;
  }>>([]);

  useEffect(() => {
    if (id) {
      loadBooking();
    }
  }, [id]);

  // Another part of the page (e.g. the scan return prompt) changed this booking.
  useEffect(() => {
    return onBookingUpdated((bookingId) => {
      if (bookingId === id) loadBooking();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle URL params for tab selection (e.g., ?tab=returnInfo)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['general', 'items', 'delivery', 'financial', 'notes', 'emails', 'messages', 'returnInfo', 'activity'].includes(tabParam)) {
      setActiveTab(tabParam as TabType);
    }
  }, [searchParams]);

  // After conflicts are resolved and state is committed, continue the save
  useEffect(() => {
    if (pendingSaveAfterConflicts) {
      setPendingSaveAfterConflicts(false);
      handleSave();
    }
  }, [pendingSaveAfterConflicts]);

  async function loadBooking() {
    if (!id) return;

    setLoading(true);
    setError(null);

    const { data, error: loadError } = await getBookingById(id, {
      merchantId,
      locationIds,
      isPlatformAdmin
    });

    if (loadError) {
      setError(loadError);
    } else if (data) {
      // If booking has no currency, fall back to merchant's currency_code
      const effectiveData = { ...data } as any;
      if (!effectiveData.currency && effectiveData.locations?.merchants?.currency_code) {
        effectiveData.currency = effectiveData.locations.merchants.currency_code;
      }
      setBooking(effectiveData);
      setOriginalBooking(effectiveData);

      // Check if return report exists
      const hasReport = await hasReturnReport(data.id);
      setReturnReportExists(hasReport);
    }

    setLoading(false);
  }

  /**
   * Re-read only the booking's items (after a scan swap), keeping any unsaved
   * edits and without the full-page loading state.
   */
  async function refreshBookingItems() {
    if (!id) return;
    const { data } = await getBookingById(id, { merchantId, locationIds, isPlatformAdmin });
    if (!data) return;
    const items = (data as any).booking_items;
    setBooking((prev) => (prev ? { ...prev, booking_items: items } : prev));
    setOriginalBooking((prev) => (prev ? { ...prev, booking_items: items } : prev));
  }

  const handleBookingChange = (updates: Partial<Booking>) => {
    if (!booking) return;

    if (!isEditable) {
      setShowEditDisabledWarning(true);
      setSaveWarning('This booking is not editable in its current state. Reopen the order to edit.');
      return;
    }

    // Check if dates changed
    const datesAreChanging = updates.start_date || updates.end_date;
    if (datesAreChanging) {
      const startChanged = updates.start_date && updates.start_date !== originalBooking?.start_date;
      const endChanged = updates.end_date && updates.end_date !== originalBooking?.end_date;
      if (startChanged || endChanged) {
        setDatesChanged(true);
      }
    }

    let updatedBooking = { ...booking, ...updates };

    // If delivery price changed, recalculate totals
    if ('delivery_price' in updates) {
      const { totalTax: newTotalTax, totalAmount: newTotalAmount } = computeBookingTotals({
        subtotal: updatedBooking.subtotal || 0,
        deliveryPrice: updates.delivery_price || 0,
        totalDiscount: updatedBooking.total_discount || 0,
        taxRate: updatedBooking.tax_rate,
        taxIncluded: updatedBooking.tax_included,
      });
      updatedBooking = { ...updatedBooking, total_tax: newTotalTax, total_amount: newTotalAmount };
    }

    // If dates changed, recalculate item prices based on new duration
    if (datesAreChanging && updatedBooking.booking_items?.length) {
      const newStart = new Date(updatedBooking.start_date);
      const newEnd = new Date(updatedBooking.end_date);
      const elapsedHours = (newEnd.getTime() - newStart.getTime()) / (1000 * 60 * 60);
      const totalHours = Math.round(elapsedHours);
      const days = Math.floor(totalHours / 24);
      const hours = totalHours % 24;

      // Recalculate prices for all items asynchronously
      recalcItemPrices(updatedBooking, days, hours, elapsedHours);
    }

    setBooking(updatedBooking);
    setHasUnsavedChanges(true);
  };

  const recalcItemPrices = async (
    currentBooking: BookingWithRelations,
    days: number,
    hours: number,
    elapsedHours?: number
  ) => {
    const items = currentBooking.booking_items || [];
    if (items.length === 0) return;

    const totalDurationHours = days * 24 + hours;
    // Bundle components are covered by their bundle's price (unit_price 0) and
    // carry the bundle's rule, so they are never priced on their own: pricing a
    // component by its own product charged it again on top of the bundle.
    const topLevelItems = items.filter((item: any) => !item.parent_booking_item_id);
    const results = await Promise.all(
      topLevelItems.map(async (item: any) => {
        const { data: pricingData } = await calculatePrice(
          item.product_type_id,
          currentBooking.location_id,
          days,
          hours,
          item.size || null,
          'admin',
          elapsedHours
        );

        if (pricingData) {
          const unitPrice = pricingData.price;
          const qty = item.quantity || 1;
          return {
            priced: true,
            item: {
              ...item,
              unit_price: unitPrice,
              price_per_unit: unitPrice,
              total_price: unitPrice * qty,
              duration_hours: totalDurationHours,
              ...pricingRuleColumns(pricingData.rule),
            },
          };
        }
        // No pricing rule covers this duration — keep the previous price, but
        // clear the rule columns: the old tariff no longer describes the row.
        return { priced: false, item: { ...item, ...NO_PRICING_RULE } };
      })
    );

    const repricedById = new Map<string, any>(results.map((r) => [r.item.id, r.item]));
    const updatedItems = items.map((item: any) => {
      if (!item.parent_booking_item_id) return repricedById.get(item.id) ?? item;
      const parent = repricedById.get(item.parent_booking_item_id);
      if (!parent) return item;
      return {
        ...item,
        unit_price: 0,
        price_per_unit: 0,
        total_price: 0,
        duration_hours: parent.duration_hours,
        ...itemRuleColumns(parent),
      };
    });
    const unpricedCount = results.filter((r) => !r.priced).length;
    setStalePriceItems(unpricedCount > 0 ? { count: unpricedCount, total: results.length } : null);

    // Recalculate totals
    const { subtotal: newSubtotal, totalTax: newTotalTax, totalAmount: newTotalAmount } =
      computeBookingTotals({
        subtotal: updatedItems.reduce((sum, item: any) => sum + (item.total_price || 0), 0),
        deliveryPrice: currentBooking.delivery_price,
        totalDiscount: currentBooking.total_discount,
        taxRate: currentBooking.tax_rate,
        taxIncluded: currentBooking.tax_included,
      });

    setBooking(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        booking_items: updatedItems as any,
        subtotal: newSubtotal,
        total_tax: newTotalTax,
        total_amount: newTotalAmount,
      };
    });
  };

  const handleItemsChange = (items: BookingItem[]) => {
    if (!booking) return;

    if (!isEditable) {
      setShowEditDisabledWarning(true);
      setSaveWarning('This booking is not editable in its current state. Reopen the order to edit.');
      return;
    }

    // Recalculate subtotal from items
    const { subtotal: newSubtotal, totalTax: newTotalTax, totalAmount: newTotalAmount } =
      computeBookingTotals({
        subtotal: items.reduce((sum, item) => sum + ((item as any).total_price || 0), 0),
        deliveryPrice: booking.delivery_price,
        totalDiscount: booking.total_discount,
        taxRate: booking.tax_rate,
        taxIncluded: booking.tax_included,
      });

    setBooking({
      ...booking,
      booking_items: items as any,
      subtotal: newSubtotal,
      total_tax: newTotalTax,
      total_amount: newTotalAmount,
    });
    setHasUnsavedChanges(true);
  };

  /**
   * Clear the test-booking flag.
   *
   * The flag is set automatically by a database trigger when the customer name
   * is exactly Test / Booking, and it is never cleared automatically — a
   * booking that stops matching keeps it, so correcting a name cannot silently
   * return a test booking to the revenue figures. This is the only way to
   * remove it, and there is deliberately no way to set it by hand: a manual
   * "mark as test" control would let anyone remove a real booking from reports
   * and from the platform-fee base. Clearing only ever makes revenue visible
   * again, which is why it needs no role restriction.
   */
  const handleClearTestFlag = async () => {
    if (!booking || !id) return;
    if (!window.confirm(t('bookings.testBookingClearConfirm'))) return;

    setClearingTestFlag(true);
    const { error: updateError } = await updateBooking(id, { is_test: false } as any);
    setClearingTestFlag(false);

    if (updateError) {
      showToast(t('bookings.testBookingClearFailed'), 'error');
      return;
    }

    await insertAuditLog({
      bookingId: id,
      merchantId: booking.merchant_id,
      locationId: booking.location_id,
      changeType: 'other',
      changeDetails: {
        action: 'test_flag_cleared',
        note: 'Booking re-included in revenue reports and the platform-fee base',
      },
      userId: user?.id || null,
      userEmail: profile?.full_name || user?.email || null,
    });

    setBooking(prev => (prev ? { ...prev, is_test: false } : prev));
    setOriginalBooking(prev => (prev ? { ...prev, is_test: false } : prev));
    showToast(t('bookings.testBookingCleared'), 'success');
  };

  const handleStateChange = async (newState: typeof BOOKING_STATES[number]) => {
    if (!booking || !id) return;

    // UI-only: clear the disabled warning once the user changes state (they likely intend to edit)
    setShowEditDisabledWarning(false);

    setSaving(true);
    setError(null);

    const previousState = booking.booking_state;
    const { data: updated, error: updateError } = await updateBooking(id, {
      booking_state: newState
    });

    if (updateError) {
      showToast('Failed to update booking state: ' + updateError, 'error');
      setError(updateError);
    } else {
      // returned_at is maintained by trg_bookings_sync_returned_at (set on End
      // order, cleared on reopen), so take it from the updated row.
      const returnedAt = updated ? updated.returned_at : booking.returned_at;
      setBooking({ ...booking, booking_state: newState, returned_at: returnedAt });
      setOriginalBooking({ ...booking, booking_state: newState, returned_at: returnedAt });
      showToast('Booking state updated successfully', 'success');

      // Log state change to audit log (fire-and-forget)
      insertAuditLog({
        bookingId: booking.id,
        merchantId: booking.merchant_id,
        locationId: booking.location_id,
        changeType: newState === 'cancelled' ? 'cancelled' : 'status_changed',
        changeDetails: {
          field: 'booking_state',
          from: previousState,
          to: newState,
        },
        userId: user?.id,
        userEmail: profile?.full_name || user?.email || null,
        triggeredBy: 'admin_panel',
      });
    }

    setSaving(false);
  };

  const stateConfig: Record<typeof BOOKING_STATES[number], { label: string; tone: string }> = {
    open: { label: t('bookings.open'), tone: 'bg-blue/10 text-blue border-blue/20' },
    active: { label: t('status.active'), tone: 'bg-success/10 text-success border-success/20' },
    returned: { label: t('bookings.completed'), tone: 'bg-success/10 text-success border-success/20' },
    cancelled: { label: t('status.cancelled'), tone: 'bg-danger/10 text-danger border-danger/20' },
  };



  const handleSave = async () => {
    if (!booking || !id) return;

    if (!isEditable) {
      setShowEditDisabledWarning(true);
      setSaveWarning('This booking is not editable in its current state. Reopen the order to edit.');
      return;
    }

    // Clear previous warnings and errors
    setSaveWarning(null);
    setValidationErrors([]);

    // Validate booking state for editing
    const errors: string[] = [];

    // Check if dates are valid
    if (booking.start_date && booking.end_date) {
      if (new Date(booking.end_date) <= new Date(booking.start_date)) {
        errors.push('End date must be after start date');
      }
    }

    // If dates changed, check availability for all existing items
    if (datesChanged && booking.booking_items && booking.booking_items.length > 0 && booking.location_id) {
      setSaving(true);
      setError(null);

      // 1. Check pool items availability
      const poolItems = (booking.booking_items as any[])
        .filter((item: any) => !item.inventory_item_id)
        .map((item: any) => ({
          product_type_id: item.product_type_id,
          item_name: item.item_name || 'Unknown item',
          quantity: item.quantity || 1,
        }));

      if (poolItems.length > 0) {
        const { data: blockedPoolItems, error: poolError } = await checkPoolItemsAvailability({
          bookingId: booking.id,
          locationId: booking.location_id,
          startDate: booking.start_date,
          endDate: booking.end_date,
          poolItems,
        });

        if (poolError) {
          errors.push(`Pool availability check failed: ${poolError}`);
          setValidationErrors(errors);
          setSaving(false);
          return;
        }

        if (blockedPoolItems && blockedPoolItems.length > 0) {
          errors.push(
            `Cannot change booking dates: The following pool items have no availability in the new timeframe: ${blockedPoolItems.join(', ')}`
          );
          setValidationErrors(errors);
          setSaving(false);
          return;
        }
      }

      // 2. Check individually-allocated items
      const bookingInventoryItems = (booking.booking_items as any[])
        .filter((item: any) => item.inventory_item_id)
        .map((item: any) => ({
          id: item.id,
          inventory_item_id: item.inventory_item_id!,
          product_type_id: item.product_type_id,
          item_name: item.item_name,
          size: item.size,
          inventory_items: item.inventory_items
        }));

      if (bookingInventoryItems.length > 0) {
        const { data: conflictingItemIds, error: availabilityError } = await checkBookingItemsAvailability({
          bookingId: booking.id,
          locationId: booking.location_id,
          startDate: booking.start_date,
          endDate: booking.end_date,
          inventoryItemIds: bookingInventoryItems.map(item => item.inventory_item_id),
        });

        if (availabilityError) {
          errors.push(`Availability check failed: ${availabilityError}`);
          setValidationErrors(errors);
          setSaving(false);
          return;
        }

        // If any items have conflicts, open the resolve-conflicts modal
        if (conflictingItemIds && conflictingItemIds.length > 0) {
          const conflicting = bookingInventoryItems
            .filter(item => conflictingItemIds.includes(item.inventory_item_id))
            .map(item => ({
              bookingItemId: item.id,
              currentInventoryItemId: item.inventory_item_id,
              productTypeId: item.product_type_id,
              productName: item.item_name || 'Unknown',
              currentSize: item.size || null,
              currentItemNumber: item.inventory_items?.item_number || item.inventory_items?.nr?.toString() || null,
            }));

          setConflictingItemsForModal(conflicting);
          setShowConflictsModal(true);
          setSaving(false);
          return;
        }
      }

      setSaving(false);
    }

    // Check if payment state changed and amounts differ
    if (originalBooking?.payment_state === 'paid' &&
        booking.total_amount !== originalBooking.total_amount) {
      setSaveWarning('The booking was already paid, but the total amount has changed. You may need to send a payment link or process a refund.');
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setSaving(true);
    setError(null);

    // Write only the columns the operator changed since the page loaded.
    // Sending every column re-asserted a stale copy of the row over anything
    // another writer (customer via the manage-booking app, payment webhook,
    // cron) had done in the meantime — see src/lib/bookingSavePayload.ts.
    // Without a loaded baseline there is nothing to diff against, so fall
    // back to the full payload rather than writing nothing.
    const updates: Partial<Booking> = originalBooking
      ? pickChangedBookingFields(originalBooking, booking)
      : pickChangedBookingFields({}, booking);

    // Totals changed locally were derived from the loaded copy. If the row's
    // money has moved on the server since then (a customer paid or edited,
    // a payment was recorded), that derivation is stale and saving it would
    // overwrite the other change. Re-read and refuse in that case; the
    // operator reloads and re-applies the edit on top of the current values.
    if (originalBooking && changedMoneyFields(updates).length > 0) {
      const { data: serverBooking, error: rereadError } = await getBookingById(id, {
        merchantId,
        locationIds,
        isPlatformAdmin,
      });

      if (rereadError || !serverBooking) {
        const message = rereadError || 'Booking could not be re-read before saving';
        showToast('Failed to update booking: ' + message, 'error');
        setError(message);
        setSaving(false);
        return;
      }

      const moved = moneyFieldsMovedSinceLoad(originalBooking, serverBooking);
      if (moved.length > 0) {
        const message = t('bookings.moneyChangedSinceLoad', { fields: moved.join(', ') });
        showToast(message, 'error');
        setError(message);
        setSaving(false);
        return;
      }
    }

    if (Object.keys(updates).length > 0) {
      const { error: updateError } = await updateBooking(id, updates);

      if (updateError) {
        showToast('Failed to update booking: ' + updateError, 'error');
        setError(updateError);
        setSaving(false);
        return;
      }
    }

    // Handle item deletions and additions
    const currentItemIds = (booking.booking_items || []).map(item => item.id);
    const originalItemIds = (originalBooking?.booking_items || []).map(item => item.id);

    // Find deleted items (items that were in original but not in current)
    const deletedItemIds = originalItemIds.filter(id => !currentItemIds.includes(id));

    // Find new items (items with temp- prefix, meaning they were added in this session)
    const newItems = (booking.booking_items || []).filter(item => item.id.startsWith('temp-'));

    // Delete removed items
    if (deletedItemIds.length > 0) {
      const { error: deleteError } = await deleteBookingItems(deletedItemIds);
      if (deleteError) {
        showToast('Failed to delete items: ' + deleteError, 'error');
        setError(`Failed to delete items: ${deleteError}`);
        setSaving(false);
        return;
      }
    }

    // Create new items (pass bundle fields through for parent/child handling)
    if (newItems.length > 0) {
      const { error: createError } = await createBookingItems(
        newItems.map(item => ({
          id: item.id, // temp ID needed for parent→child mapping
          booking_id: booking.id,
          product_type_id: item.product_type_id,
          item_name: item.item_name,
          size: item.size,
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          inventory_item_id: item.inventory_item_id,
          duration_hours: item.duration_hours || 0,
          total_price: item.total_price || 0,
          is_bundle_parent: item.is_bundle_parent || false,
          parent_booking_item_id: item.parent_booking_item_id || null,
          product_type: item.product_type || null,
          ruleColumns: itemRuleColumns(item),
        }))
      );
      if (createError) {
        showToast('Failed to create items: ' + createError, 'error');
        setError(`Failed to create items: ${createError}`);
        setSaving(false);
        return;
      }
    }

    // Update existing items whose pricing changed (e.g. after duration change)
    const originalItemsMap = new Map(
      (originalBooking?.booking_items || []).map(item => [item.id, item])
    );
    const changedItems = (booking.booking_items || []).filter(item => {
      if (item.id.startsWith('temp-')) return false; // new items already handled above
      const orig = originalItemsMap.get(item.id);
      if (!orig) return false;
      return item.unit_price !== orig.unit_price
        || item.total_price !== orig.total_price
        || item.duration_hours !== orig.duration_hours
        || (item.quantity || 1) !== (orig.quantity || 1)
        || ruleColumnsChanged(item, orig);
    });

    if (changedItems.length > 0) {
      const { error: updateItemsError } = await updateBookingItems(
        changedItems.map(item => ({
          id: item.id,
          unit_price: item.unit_price || 0,
          total_price: item.total_price || 0,
          duration_hours: item.duration_hours || 0,
          quantity: item.quantity || 1,
          // Written only when the tariff changed; otherwise left untouched.
          ...(ruleColumnsChanged(item, originalItemsMap.get(item.id)!)
            ? { ruleColumns: itemRuleColumns(item) }
            : {}),
        }))
      );
      if (updateItemsError) {
        showToast('Failed to update item pricing: ' + updateItemsError, 'error');
        setError(`Failed to update item pricing: ${updateItemsError}`);
        setSaving(false);
        return;
      }
    }

    // Build audit log entries for changed fields (fire-and-forget)
    if (originalBooking) {
      const auditEntries: Array<Parameters<typeof insertAuditLogBatch>[0][number]> = [];
      const baseEntry = {
        bookingId: booking.id,
        merchantId: booking.merchant_id,
        locationId: booking.location_id,
        userId: user?.id,
        userEmail: profile?.full_name || user?.email || null,
        triggeredBy: 'admin_panel' as const,
      };

      // Status changes
      if (booking.booking_state !== originalBooking.booking_state) {
        auditEntries.push({
          ...baseEntry,
          changeType: booking.booking_state === 'cancelled' ? 'cancelled' : 'status_changed',
          changeDetails: { field: 'booking_state', from: originalBooking.booking_state, to: booking.booking_state },
        });
      }
      if (booking.payment_state !== originalBooking.payment_state) {
        auditEntries.push({
          ...baseEntry,
          changeType: 'payment_updated',
          changeDetails: { field: 'payment_state', from: originalBooking.payment_state, to: booking.payment_state },
        });
      }

      // Date changes
      if (booking.start_date !== originalBooking.start_date || booking.end_date !== originalBooking.end_date) {
        auditEntries.push({
          ...baseEntry,
          changeType: 'dates_changed',
          changeDetails: {
            from: { start_date: originalBooking.start_date, end_date: originalBooking.end_date },
            to: { start_date: booking.start_date, end_date: booking.end_date },
          },
        });
      }

      // Customer info changes
      const customerFields: Array<keyof Booking> = ['customer_firstname', 'customer_lastname', 'customer_email', 'customer_phone'];
      const customerChanges: Record<string, { from: any; to: any }> = {};
      for (const key of customerFields) {
        if (String(originalBooking[key] ?? '') !== String(booking[key] ?? '')) {
          customerChanges[key] = { from: originalBooking[key], to: booking[key] };
        }
      }
      if (Object.keys(customerChanges).length > 0) {
        auditEntries.push({
          ...baseEntry,
          changeType: 'customer_info_updated',
          changeDetails: customerChanges,
        });
      }

      // Notes changes
      if (String(booking.internal_notes ?? '') !== String(originalBooking.internal_notes ?? '')) {
        auditEntries.push({
          ...baseEntry,
          changeType: 'notes_added',
          changeDetails: { field: 'internal_notes', from: originalBooking.internal_notes, to: booking.internal_notes },
        });
      }

      // Financial changes (amount, discount, delivery price)
      const financialFields: Array<keyof Booking> = ['total_amount', 'total_discount', 'delivery_price'];
      const financialChanges: Record<string, { from: any; to: any }> = {};
      for (const key of financialFields) {
        if (String(originalBooking[key] ?? '') !== String(booking[key] ?? '')) {
          financialChanges[key] = { from: originalBooking[key], to: booking[key] };
        }
      }
      if (Object.keys(financialChanges).length > 0) {
        auditEntries.push({
          ...baseEntry,
          changeType: 'other',
          changeDetails: { description: 'Financial details updated', changes: financialChanges },
        });
      }

      // Item removals
      if (deletedItemIds.length > 0) {
        const deletedNames = (originalBooking.booking_items || [])
          .filter(item => deletedItemIds.includes(item.id))
          .map((item: any) => item.item_name || 'Unknown');
        auditEntries.push({
          ...baseEntry,
          changeType: 'items_removed',
          changeDetails: { items: deletedNames },
        });
      }

      // Item additions
      if (newItems.length > 0) {
        const addedNames = newItems.map((item: any) => item.item_name || 'Unknown');
        auditEntries.push({
          ...baseEntry,
          changeType: 'items_added',
          changeDetails: { items: addedNames },
        });
      }

      if (auditEntries.length > 0) {
        insertAuditLogBatch(auditEntries);
      }
    }

    showToast('Booking updated successfully', 'success');
    setHasUnsavedChanges(false);
    setDatesChanged(false);
    setStalePriceItems(null);
    // Reload booking to get fresh data from DB
    await loadBooking();
    setSaveWarning(null);
    setSaving(false);
  };

  const handleConflictsResolved = (resolutions: Array<{
    bookingItemId: string;
    newInventoryItemId: string;
    newItemData: { nr: number | null; item_number: string | null; combination_code: string | null; size: string | null };
    skipBufferCheck: boolean;
  }>) => {
    if (!booking) return;

    // Update local booking items with the new inventory assignments
    const updatedItems = (booking.booking_items || []).map((item: any) => {
      const resolution = resolutions.find(r => r.bookingItemId === item.id);
      if (resolution) {
        return {
          ...item,
          inventory_item_id: resolution.newInventoryItemId,
          inventory_items: resolution.newItemData,
          size: resolution.newItemData.size,
        };
      }
      return item;
    });

    setBooking({
      ...booking,
      booking_items: updatedItems as any,
    });

    // The RPC already persisted the inventory_item_id changes in the DB.
    // Now auto-continue the save to persist the date change and other fields.
    // Use a state flag so handleSave runs after React commits the updated state
    // (avoids stale closure where datesChanged is still true).
    setShowConflictsModal(false);
    setDatesChanged(false); // Prevent re-checking conflicts on the resumed save
    setHasUnsavedChanges(true);
    showToast('Item conflicts resolved. Saving booking...', 'success');
    setPendingSaveAfterConflicts(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 text-blue animate-spin" />
        <span className="ml-md text-secondary">{t('bookings.loadingBooking')}</span>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="max-w-2xl mx-auto mt-2xl">
        <div className="p-lg bg-danger/10 border-l-4 border-danger rounded-lg">
          <div className="flex items-start gap-md">
            <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-danger mb-xs">{t('bookings.errorLoading')}</h4>
              <p className="text-sm text-secondary">{error}</p>
              <button onClick={() => navigate('/bookings')} className="btn btn-sm btn-primary mt-md">
                {t('bookings.backToBookings')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  const canEdit = hasRole('operator');
  const isEditable = canEdit && booking.booking_state === 'open';
  const showReturnInfoTab = returnReportExists;

  const getStateBadgeClass = (state: string) => {
    switch (state) {
      case 'open': return 'badge-info';
      case 'active': return 'badge-success';
      case 'returned': return 'badge-success';
      case 'cancelled': return 'badge-danger';
      default: return 'badge-info';
    }
  };

  const getPaymentBadgeClass = (state: string) => {
    switch (state) {
      case 'paid': return 'badge-success';
      case 'open': return 'badge-warning';
      case 'open_pay_later': return 'badge-warning';
      case 'refunded': return 'badge-info';
      case 'partially_refunded': return 'badge-danger';
      default: return 'badge-info';
    }
  };

  const tabs: Array<{ id: TabType; label: string; visible: boolean }> = [
    { id: 'general', label: t('bookings.general'), visible: true },
    { id: 'items', label: t('bookings.items'), visible: true },
    { id: 'delivery', label: t('bookings.delivery'), visible: booking.locations?.deliveries ?? false },
    { id: 'financial', label: t('bookings.financial'), visible: true },
    { id: 'notes', label: t('bookings.notes'), visible: true },
    { id: 'emails', label: t('bookings.emails'), visible: true },
    { id: 'messages', label: t('bookings.messages'), visible: !!(booking.locations?.merchants?.sms || booking.locations?.merchants?.whatsapp) },
    { id: 'returnInfo', label: t('bookings.returnInfo'), visible: showReturnInfoTab },
    { id: 'activity', label: t('bookings.activity'), visible: true },
  ];

  return (
    <div className="space-y-lg animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-md">
        <div>
          <button
            onClick={() => navigate('/bookings')}
            className="flex items-center gap-xs text-secondary hover:text-primary mb-md transition-colors"
            data-testid="booking-edit-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('bookings.backToOrders')}
          </button>
          <div className="flex flex-wrap items-center gap-md mb-xs">
            <h1 className="text-3xl font-bold text-primary">
              {t('bookings.order')} #{booking.order_id}
            </h1>
            {/* Test-booking marker. Visible on purpose: a flagged booking is
                absent from every revenue figure and from the platform-fee base,
                and without something on screen saying so a false positive is
                undetectable. */}
            {(booking as any).is_test && (
              <span
                className="inline-flex items-center gap-xs px-md py-xs rounded-full bg-warning/10 text-warning border border-warning/30 text-sm font-medium"
                title={t('bookings.testBookingHint')}
                data-testid="booking-edit-test-badge"
              >
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {t('bookings.testBooking')}
                <button
                  type="button"
                  onClick={handleClearTestFlag}
                  disabled={clearingTestFlag}
                  className="ml-xs underline hover:no-underline disabled:opacity-50"
                  data-testid="booking-edit-test-badge-clear"
                >
                  {clearingTestFlag ? t('common.saving') : t('bookings.testBookingClear')}
                </button>
              </span>
            )}
            {/* Primary status action button - next to order number */}
            {canEdit && booking.booking_state === 'open' && (
              <button
                type="button"
                onClick={() => {
                  // Scanning merchants: warn (never block) when items weren't scanned.
                  if (scanningEnabled && pickupUnticked.length > 0) {
                    setShowStartWarning(true);
                    return;
                  }
                  handleStateChange('active');
                }}
                disabled={saving}
                className="btn btn-primary px-8"
                style={{ minWidth: '120px' }}
                data-testid="booking-edit-status-primary-start-button"
              >
                {t('bookings.startAction')}
              </button>
            )}
            {canEdit && booking.booking_state === 'active' && (
              <button
                type="button"
                onClick={() => handleStateChange('returned')}
                disabled={saving}
                className="btn btn-primary px-8"
                style={{ minWidth: '120px' }}
                data-testid="booking-edit-status-primary-end-button"
              >
                {t('bookings.endOrder')}
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-md">
            <div className="flex items-center gap-xs" data-testid="booking-edit-status-control">
              <span className="text-sm text-secondary">{t('bookings.statusLabel')}</span>
              <span className={`badge ${getStateBadgeClass(booking.booking_state)}`}>
                {stateConfig[booking.booking_state as keyof typeof stateConfig]?.label || booking.booking_state}
              </span>
            </div>

            <div className="flex items-center gap-xs" data-testid="booking-edit-payment-badge">
              <span className="text-sm text-secondary">{t('bookings.paymentLabel')}</span>
              <span className={`badge ${getPaymentBadgeClass(booking.payment_state)}`}>
                {booking.payment_state.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </span>
            </div>
          </div>
        </div>
        {canEdit && (
        <div className="flex flex-wrap items-center gap-md justify-end" data-testid="booking-edit-status-actions">
          {/* Secondary status actions (links) */}
          {booking.booking_state === 'open' && (
            <button
              type="button"
              onClick={() => handleStateChange('cancelled')}
              disabled={saving}
              className="text-sm text-danger hover:underline"
              data-testid="booking-edit-status-secondary-cancel-link"
            >
              {t('bookings.cancelBooking')}
            </button>
          )}

          {booking.booking_state === 'active' && (
            <button
              type="button"
              onClick={() => handleStateChange('open')}
              disabled={saving}
              className="text-sm text-secondary hover:underline"
              data-testid="booking-edit-status-secondary-cancel-start-link"
            >
              {t('bookings.cancelStart')}
            </button>
          )}

          {(booking.booking_state === 'returned' || booking.booking_state === 'cancelled') && (
            <button
              type="button"
              onClick={() => handleStateChange('open')}
              disabled={saving}
              className="text-sm text-secondary hover:underline"
              data-testid="booking-edit-status-reopen-link"
            >
              {t('bookings.reopenOrder')}
            </button>
          )}

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={!hasUnsavedChanges || saving || validationErrors.length > 0}
            className="btn btn-primary"
            data-testid="booking-edit-save-button"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('common.saving')}...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {t('actions.saveChanges')}
              </>
            )}
          </button>
        </div>
        )}
      </div>

      {/* Not Editable Warning (shown only after user attempts to edit) */}
      {!isEditable && showEditDisabledWarning && (
        <div className="p-md bg-warning/10 border-l-4 border-warning rounded-lg" data-testid="booking-edit-disabled-warning">
          <div className="flex items-start gap-md">
            <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-warning mb-xs">{t('bookings.editingDisabled')}</h4>
              <p className="text-sm text-secondary">
                {t('bookings.editingDisabledMessage', { state: booking.booking_state })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Save Warning */}
      {saveWarning && (
        <div className="p-md bg-warning/10 border-l-4 border-warning rounded-lg">
          <div className="flex items-start gap-md">
            <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-warning mb-xs">{t('bookings.warning')}</h4>
              <p className="text-sm text-secondary">{saveWarning}</p>
            </div>
          </div>
        </div>
      )}

      {/* Dates Changed Warning */}
      {datesChanged && (activeTab === 'items' || activeTab === 'general') && (
        <div className="p-md bg-warning/10 border-l-4 border-warning rounded-lg">
          <div className="flex items-start gap-md">
            <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-warning mb-xs">{t('bookings.datesChanged')}</h4>
              <p className="text-sm text-secondary">
                {t('bookings.datesChangedMessage')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stale price warning: items with no pricing rule for the new duration */}
      {stalePriceItems && (activeTab === 'items' || activeTab === 'general') && (
        <div className="p-md bg-warning/10 border-l-4 border-warning rounded-lg">
          <div className="flex items-start gap-md">
            <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-warning mb-xs">{t('bookings.priceTierMissing')}</h4>
              <p className="text-sm text-secondary">
                {t('bookings.priceTierMissingMessage', { count: stalePriceItems.count, total: stalePriceItems.total })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg">
          <div className="flex items-start gap-md">
            <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-danger mb-xs">{t('errors.validationErrors')}</h4>
              <ul className="list-disc list-inside text-sm text-secondary">
                {validationErrors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {scanningEnabled && canEdit && booking.booking_state === 'open' && (
        <PickupChecklist
          booking={booking}
          onBookingChanged={refreshBookingItems}
          initialScan={pickupScan}
          // Drop the handed-over scan from history so a page refresh doesn't replay it.
          onInitialScanConsumed={() => navigate(routerLocation.pathname + routerLocation.search, { replace: true, state: null })}
          onProgress={setPickupUnticked}
        />
      )}

      {showStartWarning && (
        <ScanModal
          title={t('scan.notAllScannedTitle')}
          onClose={() => setShowStartWarning(false)}
          testId="pickup-start-warning"
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowStartWarning(false)}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowStartWarning(false);
                  handleStateChange('active');
                }}
              >
                {t('scan.startAnyway')}
              </button>
            </>
          }
        >
          <p className="text-sm text-secondary">{t('scan.notAllScannedPickup')}</p>
          <ul className="list-disc list-inside text-sm text-primary mt-sm">
            {pickupUnticked.map((line) => <li key={line.id}>{lineLabel(line)}</li>)}
          </ul>
        </ScanModal>
      )}

      {/* Tabs */}
      <div className="card p-0 overflow-hidden">
        <div className="flex border-b border-primary overflow-x-auto">
          {tabs.filter(tab => tab.visible).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-lg py-md text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'text-blue border-b-2 border-blue bg-blue/5'
                  : 'text-secondary hover:text-primary hover:bg-secondary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-lg">
          {activeTab === 'general' && (
            <BookingGeneralForm
              booking={booking}
              onChange={handleBookingChange}
              disabled={!isEditable}
            />
          )}
          {activeTab === 'items' && (
            <BookingItemsEditor
              booking={booking}
              items={booking.booking_items || []}
              extensions={booking.booking_extensions || []}
              onChange={handleItemsChange}
              disabled={!isEditable}
              merchantId={merchantId || ''}
              currencySymbol={getCurrencySymbol(booking.currency || (booking as any).locations?.merchants?.currency_code)}
            />
          )}
          {activeTab === 'delivery' && (
            <BookingDeliveryForm
              booking={booking}
              onChange={handleBookingChange}
              disabled={!isEditable}
            />
          )}
          {activeTab === 'financial' && (
            <BookingFinancialView
              booking={booking}
              originalBooking={originalBooking}
              onChange={handleBookingChange}
              disabled={!isEditable}
              onRefundProcessed={() => loadBooking()}
              onPaymentRecorded={() => loadBooking()}
            />
          )}
          {activeTab === 'notes' && (
            <BookingNotesForm
              booking={booking}
              onChange={handleBookingChange}
              disabled={!isEditable}
            />
          )}
          {activeTab === 'emails' && (
            <BookingEmailsTab
              bookingId={booking.id}
              bookingState={booking.booking_state}
              paymentState={booking.payment_state}
              language={booking.language || 'en'}
              selfServiceEnabled={booking.locations?.self_service_enabled ?? false}
              isDeliveryLocation={booking.locations?.deliveries ?? false}
              twoEmailsEnabled={booking.locations?.two_emails_enabled ?? false}
              endDate={booking.end_date}
            />
          )}
          {activeTab === 'messages' && (
            <BookingMessagesTab
              bookingId={booking.id}
              smsEnabled={booking.locations?.merchants?.sms ?? false}
              whatsappEnabled={booking.locations?.merchants?.whatsapp ?? false}
            />
          )}
          {activeTab === 'returnInfo' && showReturnInfoTab && (
            <ReturnReportDetail bookingId={booking.id} locationId={booking.location_id} />
          )}
          {activeTab === 'activity' && (
            <BookingAuditLog bookingId={booking.id} />
          )}
        </div>
      </div>

      {/* Resolve Conflicts Modal */}
      <ResolveConflictsModal
        isOpen={showConflictsModal}
        onClose={() => setShowConflictsModal(false)}
        conflictingItems={conflictingItemsForModal}
        bookingId={booking.id}
        locationId={booking.location_id}
        merchantId={merchantId || ''}
        startDate={booking.start_date}
        endDate={booking.end_date}
        onResolved={handleConflictsResolved}
      />
    </div>
  );
}
